import React, { useEffect, useState } from "react";
import {
  Typography,
  TextField,
  IconButton,
  Box,
  Button,
  Container,
  CircularProgress,
  Input,
  Breadcrumbs,
  Link as MUILink,
} from "@mui/material";
import { Link } from "react-router-dom";
import moment from "moment";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { message, Modal } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PageTitle from "../../Components/PageTitle";
import Table from "../common/Table";
import axios from "../../axiosInstance";
import EditAcademicExamTimeTable from "./EditAcademicExamTimeTable";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const AcademicExamTimeTable = () => {
  const [value, setValue] = useState(0);
  const [search, setSearch] = useState("");
  const [academicExamTimeTable, setAcademicExamTimeTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredExamTimeTable, setFilteredExamTimeTable] = useState([]);
  const [file, setFile] = useState(null);
  const [desc, setDesc] = useState("");
  const [title, setTitle] = useState("");

  const handleChange = (desc, newValue) => {
    setValue(newValue);
  };

  // Get Data Academic Exam Time Table
  const getAcademicExamTimeTable = async () => {
    try {
      const response = await axios.get("/api/v1/academic-exam-time-table/get");
      // Check if the response is successful
      if (response.data.success === false) {
        message.error("Failed to fetch Academic Exam Time Table");
        return;
      }
      // Set the data to state
      setAcademicExamTimeTable(response.data?.examTimeTable);
      setFilteredExamTimeTable(response.data?.examTimeTable);
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  useEffect(() => {
    getAcademicExamTimeTable();
  }, []);

  // Function to refresh the academic Exam Time Table data
  const refreshData = () => {
    getAcademicExamTimeTable(); // Refresh data after update
  };

  // Handle search input change
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Filter academic exam time table based on search query
  const filteredNewAcademic = filteredExamTimeTable?.filter((academic) =>
    academic?.title?.toLowerCase()?.includes(search?.toLowerCase())
  );

  // Handle Delete Api Academic exam time table
  const { confirm } = Modal;
  const handleDeleteAcademicExamTimeTable = async (cloudinaryId) => {
    confirm({
      title: "Are you sure you want to delete this Academic Exam Time Table?",
      content: "Once deleted, this course cannot be recovered.",
      okText: "Yes, Delete",
      cancelText: "No, Cancel",
      onOk: async () => {
        try {
          const response = await axios.delete(
            `/api/v1/academic-exam-time-table/admin-delete`,
            { data: { cloudinaryId } }
          );
          // Check if the response is successful
          if (response.data.success === true) {
            message.success(
              response.data.message ||
                "Academic Exam Time Table deleted successfully"
            );
            getAcademicExamTimeTable();
          } else {
            message.error("Failed to delete Academic Exam Time Table");
          }
        } catch (error) {
          message.error(
            error.response.data.message ||
              "Error deleting academic exam time table"
          );
        }
      },
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileSizeInMB = selectedFile.size / (1024 * 1024);
      if (fileSizeInMB > 50) {
        message.error(
          "File size exceeds the limit. Please upload a file with a maximum size of 50MB."
        );
        return;
      }
      if (selectedFile.type !== "application/pdf") {
        message.error("Invalid file type. Please upload a PDF file.");
        return;
      }
      setFile(selectedFile);
    }
  };

  // Add PDF notes
  // Handle Api Academic Exam Time Table
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!title) {
      message.error("Please enter a title");
      return;
    }
    if (!desc) {
      message.error("Please enter a academic exam time table description");
      return;
    }
    const formData = new FormData();
    formData.append("title", title);
    formData.append("desc", desc);
    formData.append("file", file);

    try {
      const { data } = await axios.post(
        "/api/v1/academic-exam-time-table/admin-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (data.success === true) {
        message.success(data.message);
        // Reset form fields
        setTitle("");
        setDesc("");
        setFile(null);
      } else {
        message.error(data?.message);
      }
    } catch (error) {
      message.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "rowNumber",
      headerName: "#",
      width: 10,
      sortable: false,
    },
    {
      field: "Actions",
      width: 120,
      renderCell: (value) => (
        <Box>
          <IconButton
            aria-label="delete"
            onClick={() =>
              handleDeleteAcademicExamTimeTable(value?.row?.cloudinaryId)
            }
          >
            <DeleteIcon sx={{ color: "red" }} />
          </IconButton>
          <EditAcademicExamTimeTable
            id={value?.row?._id}
            examTimeTableTitle={value?.row?.title}
            examTimeTableDesc={value?.row?.desc}
            refreshData={refreshData}
          />
        </Box>
      ),
    },

    {
      field: "title",
      headerName: "Title",
      width: 180,
      editable: true,
    },
    {
      field: "desc",
      headerName: "Description",
      width: 350,
      renderCell: (params) => (
        <div
          style={{
            display: "-webkit-box",
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 1,
          }}
          dangerouslySetInnerHTML={{ __html: params.row.desc }}
        />
      ),
    },

    {
      field: "createdAt",
      headerName: "CreatedAt",
      width: 150,
      renderCell: (params) =>
        moment(params?.row?.createdAt).format("MM/DD/YYYY"),
    },
  ];

  return (
    <>
      <PageTitle
        title="Manage Exam Time Table - Admin Panel | Shree Mangalam School"
        description="Create and manage academic exam schedules through the Admin Panel of Shree Mangalam School. Ensure accurate and timely publishing of exam time tables for all classes."
        url="/admin/academic-exam-time-table"
        keywords="exam time table admin, manage school exams, academic exam schedule, Shree Manglam timetable, admin exam planning, school exam calendar"
      />

      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: "Poppins, sans-serif",
          color: "#2C3E50",
          letterSpacing: "2.5px",
          lineHeight: 1.8,
        }}
      >
        Academic Exam Time Table
      </Typography>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 5,
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <Typography sx={{ color: "primary.main" }}>
          Academic Calendar
        </Typography>
      </Breadcrumbs>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab label="Time Table List" {...TabPanel(0)} />
          <Tab label="Add Time Table" {...TabPanel(1)} />
        </Tabs>
      </Box>
      {/* Events & News List */}
      <CustomTabPanel value={value} index={0}>
        <Typography
          variant="h6"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Exam Time Table List
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by Name or Event"
          value={search}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <IconButton>
                <SearchIcon sx={{ color: "#1976d2" }} />
              </IconButton>
            ),
          }}
          sx={{
            mb: 3,
            mt: 2,
            bgcolor: "white",
            borderRadius: "20px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
          }}
        />
        <Table data={filteredNewAcademic} columns={columns} />
      </CustomTabPanel>

      {/* Create a New Add Events & News */}
      <CustomTabPanel value={value} index={1} maxWidth>
        <Typography
          variant="h6"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Create a new Exam Time Table
        </Typography>
        <Container
          onSubmit={handleSubmit}
          component="form"
          className="form_style border-style"
          maxWidth
        >
          <TextField
            sx={{
              mb: 3,
              "& .MuiInputBase-root": {
                color: "text.secondary",
              },
              fieldset: { borderColor: "rgb(231, 235, 240)" },
            }}
            fullWidth
            id="title"
            label="Title *"
            name="title"
            InputLabelProps={{
              shrink: true,
            }}
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <h4>Description </h4>
          <ReactQuill
            value={desc}
            onChange={setDesc}
            placeholder="Description"
            style={{
              // height: "200px",
              marginBottom: "30px",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: "10px",
            }}
          />

          <Box sx={{ mb: 4 }}>
            <label htmlFor="upload-file">
              <Input
                accept="application/pdf"
                id="upload-file"
                type="file"
                style={{ display: "none" }}
                onChange={handleFileChange}
                required
              />
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
              >
                Upload PDF File Time Table
              </Button>
            </label>
            {file && (
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                {file.name}
              </Typography>
            )}
          </Box>

          <Button
            disabled={loading}
            type="submit"
            variant="contained"
            color="primary"
            className="courses_desc"
            sx={{
              mt: 3,
              borderRadius: "5px",
              textTransform: "none",
              fontFamily: "Poppins, sans-serif",
              letterSpacing: ".1rem",
            }}
          >
            {loading ? <CircularProgress size={24} /> : " Add Exam Time Table"}
          </Button>
        </Container>
      </CustomTabPanel>
    </>
  );
};

export default AcademicExamTimeTable;
